import type {GridApiCommon} from '@mui/x-data-grid';
import type {GridDensity, GridFilterModel, GridRowClassNameParams} from '@mui/x-data-grid-premium';
import type {RefObject} from 'react';
import {gridRowNodeSelector} from '@mui/x-data-grid-premium';

/**
 * MUI density multipliers applied to `rowHeight`, `columnHeaderHeight` and `headerFilterHeight`.
 * Compact / comfortable match `@mui/x-data-grid` `COMPACT_DENSITY_FACTOR` / `COMFORTABLE_DENSITY_FACTOR`.
 */
export const DATA_GRID_DENSITY_FACTOR = {
    compact: 0.7,
    standard: 1,
    comfortable: 1.3,
} as const;

export const COMPACT_DENSITY_FACTOR = DATA_GRID_DENSITY_FACTOR.compact;

/** Default density — compact rows (36px with `rowSize="small"`). */
export const DATA_GRID_DENSITY: GridDensity = 'compact';

export const DATA_GRID_ROW_MIN_HEIGHT = {
    small: 36,
    medium: 72,
} as const;

/** Rendered column-header height at compact density (MUI default 56 × 0.7, floored). */
export const DATA_GRID_COLUMN_HEADER_HEIGHT = Math.floor(56 * DATA_GRID_DENSITY_FACTOR.compact);

/** Rendered height of the header filter row (input filters). */
export const DATA_GRID_HEADER_FILTER_HEIGHT = 52;

/** Toolbar height — pinned regardless of density. */
export const DATA_GRID_TOOLBAR_HEIGHT = 44;

/** Pagination bar height — pinned regardless of density. */
export const DATA_GRID_PAGINATION_HEIGHT = DATA_GRID_ROW_MIN_HEIGHT.small;

export type DataGridRowSize = keyof typeof DATA_GRID_ROW_MIN_HEIGHT;

export type DataGridRowMinHeight = (typeof DATA_GRID_ROW_MIN_HEIGHT)[DataGridRowSize];

export const getDataGridRowMinHeight = (size: DataGridRowSize): DataGridRowMinHeight =>
    DATA_GRID_ROW_MIN_HEIGHT[size];

export const getDataGridMinRowClassName = (minHeight: DataGridRowMinHeight): string =>
    `data-grid-min-row-${minHeight}`;

/**
 * Prop value so MUI's `Math.floor(value * densityFactor)` yields `targetHeight`.
 * Header / filter-row heights use this so chrome stays put when row density changes.
 */
export const getDataGridHeightForDensity = (targetHeight: number, density: GridDensity): number =>
    Math.ceil(targetHeight / DATA_GRID_DENSITY_FACTOR[density]);

/**
 * Prop value so compact density (×0.7) produces the target rendered height.
 * Used for `rowHeight` from `rowSize`, so density can still scale rows.
 */
export const getDataGridHeightForCompactMin = (targetHeight: number): number =>
    getDataGridHeightForDensity(targetHeight, 'compact');

export const buildDataGridClassName = (
    options: {
        minHeight: DataGridRowMinHeight;
        autoRowHeight?: boolean;
        extra?: string;
    },
): string => {
    const parts = [
        options.autoRowHeight ? 'base-table-muix auto-row-height-centered' : undefined,
        getDataGridMinRowClassName(options.minHeight),
        options.extra,
    ].filter(Boolean);

    return parts.join(' ');
};

export const DATA_GRID_GROUP_ROW_EXPANDED_CLASS = 'data-grid-group-row-expanded';
export const DATA_GRID_GROUP_CHILD_ROW_CLASS = 'data-grid-group-child-row';

/** Row grouping: expanded group rows and their visible child rows. */
export const getDataGridGroupingRowClassName = (
    apiRef: RefObject<GridApiCommon | null>,
    params: GridRowClassNameParams,
): string => {
    const rowNode = gridRowNodeSelector(apiRef, params.id);
    if (!rowNode) {
        return '';
    }

    if (rowNode.type === 'group' && rowNode.childrenExpanded) {
        return DATA_GRID_GROUP_ROW_EXPANDED_CLASS;
    }

    if (rowNode.type === 'leaf' && rowNode.depth > 0) {
        return DATA_GRID_GROUP_CHILD_ROW_CLASS;
    }

    return '';
};

/** Page sizes offered by every server-paginated data grid; the first entry is the default. */
export const DATA_GRID_PAGE_SIZE_OPTIONS = [25, 50, 100, 500] as const;

export const DATA_GRID_DEFAULT_PAGE_SIZE: number = DATA_GRID_PAGE_SIZE_OPTIONS[0];

/** Minimal empty filter model for controlled `filterModel` state. */
export const EMPTY_FILTER_MODEL: GridFilterModel = {items: []};
